import { request } from './client';
import type {
  KbArticle,
  KbCategory,
  KbOwner,
  KbShelf,
  KbTerm,
  KbTermInput,
} from '../types';

/** Where a shelf of knowledge lives on the API. */
function shelfPath(owner: KbOwner): string {
  if (owner.kind === 'company') return `/companies/${owner.id}/knowledge`;
  if (owner.kind === 'well') return `/wells/${owner.id}/knowledge`;
  return '/kb/articles';
}

/** The knowledge base: glossary terms and article shelves. */
export const kbApi = {
  categories: async () => (await request<{ items: KbCategory[] }>('/kb/categories')).items,

  terms: async (q = '', category = '') => {
    const params = new URLSearchParams({ limit: '200' });
    if (q) params.set('q', q);
    if (category) params.set('category', category);
    return (await request<{ items: KbTerm[] }>(`/kb/terms?${params}`)).items;
  },

  createTerm: (body: KbTermInput) =>
    request<KbTerm>('/kb/terms', { method: 'POST', body: JSON.stringify(body) }),

  updateTerm: (slug: string, body: KbTermInput) =>
    request<KbTerm>(`/kb/terms/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteTerm: (slug: string) =>
    request<void>(`/kb/terms/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  /**
   * One shelf of articles: the general one, a company's or a well's. The
   * server says whether the caller may change it. An API from before shelves
   * existed says nothing; `editable` is then left out, for the caller to fall
   * back on the rule of that time — the superadmin, on the general shelf.
   */
  shelf: async (owner: KbOwner): Promise<Omit<KbShelf, 'editable'> & { editable?: boolean }> => {
    const res = await request<Partial<KbShelf>>(shelfPath(owner));
    const items = res.items ?? [];
    return { items, total: res.total ?? items.length, editable: res.editable };
  },

  /** Written onto the shelf it is created on; an article never moves. */
  createArticle: (body: { title: string; body: string }, owner: KbOwner = { kind: 'general' }) =>
    request<KbArticle>(shelfPath(owner), { method: 'POST', body: JSON.stringify(body) }),

  updateArticle: (id: number, body: { title: string; body: string }) =>
    request<KbArticle>(`/kb/articles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteArticle: (id: number) => request<void>(`/kb/articles/${id}`, { method: 'DELETE' }),
};
